import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { baseUrl } from '../config';
import SEO from '../components/SEO';
import { Loader2, Film, CheckCircle, Clock, XCircle, Plus, X, Building2, Send } from 'lucide-react';

const createAuthAxios = () => {
  const token = localStorage.getItem('access_token');
  return axios.create({
    baseURL: baseUrl,
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
  });
};

const statusConfig = {
  'active': { label: 'In Progress', color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-400', bar: 'bg-yellow-400', Icon: Clock },
  'completed': { label: 'Completed', color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-400', bar: 'bg-green-500', Icon: CheckCircle },
  'cancelled': { label: 'Cancelled', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-400', bar: 'bg-red-400', Icon: XCircle },
};

const MyProjects = ({ productionHouse }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [newProgress, setNewProgress] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [updating, setUpdating] = useState(false);

  // Individual project creation (no PH)
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ project_name: '', role: '', status: 'active' });
  const [adding, setAdding] = useState(false);

  // Production house allocation request
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({ role: '', message: '' });
  const [requesting, setRequesting] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState('');

  const fetchProjects = async () => {
    setLoading(true);
    setError('');
    try {
      const api = createAuthAxios();
      const res = await api.get('/enthusiast/projects');
      setProjects(res.data || []);
    } catch (err) {
      setError('Failed to load projects.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;
    setUpdating(true);
    try {
      const api = createAuthAxios();
      const res = await api.put(
        `/enthusiast/projects/${selectedProject.enthusiast_project_id}`,
        { progress: parseInt(newProgress), status: newStatus }
      );
      setProjects(prev =>
        prev.map(p => p.enthusiast_project_id === selectedProject.enthusiast_project_id ? res.data : p)
      );
      setSelectedProject(null);
    } catch (err) {
      setError('Failed to update project.');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const api = createAuthAxios();
      const res = await api.post('/enthusiast/projects', addForm);
      setProjects(prev => [res.data, ...prev]);
      setShowAddForm(false);
      setAddForm({ project_name: '', role: '', status: 'active' });
    } catch (err) {
      setError('Failed to add project.');
    } finally {
      setAdding(false);
    }
  };

  const handleRequestAllocation = async (e) => {
    e.preventDefault();
    setRequesting(true);
    setRequestSuccess('');
    try {
      const api = createAuthAxios();
      await api.post('/production/request-allocation', {
        role: requestForm.role,
        message: requestForm.message,
      });
      setRequestSuccess('Request sent to your production house admin successfully!');
      setTimeout(() => {
        setShowRequestForm(false);
        setRequestSuccess('');
        setRequestForm({ role: '', message: '' });
      }, 2500);
    } catch (err) {
      setError('Failed to send request. Please try again.');
    } finally {
      setRequesting(false);
    }
  };

  const openRequestForm = () => setShowRequestForm(true);

  useEffect(() => { fetchProjects(); }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-teal-600" size={40} />
        <span className="ml-3 text-gray-600 font-medium">Loading projects...</span>
      </div>
    );
  }

  return (
    <div className="p-4">
      <SEO
        title="My Film Projects"
        description="View and manage all your film production projects in one place on ProducerBuddy."
        url="https://producerbuddy.pilomation.com/myprojects"
        robots="noindex, nofollow"
      />
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Film className="text-teal-600" size={28} /> My Projects
        </h2>

        {productionHouse ? (
          <button
            onClick={openRequestForm}
            className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-teal-700 transition-colors shadow whitespace-nowrap"
          >
            <Building2 size={18} /> Request Project Allocation
          </button>
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-teal-700 transition-colors shadow whitespace-nowrap"
          >
            <Plus size={18} /> Add Project
          </button>
        )}
      </div>

      {/* Production House Badge */}
      {productionHouse && (
        <div className="mb-6 p-4 bg-teal-50 border-2 border-teal-200 rounded-xl flex items-center gap-3">
          <Building2 className="text-teal-600 flex-shrink-0" size={24} />
          <div>
            <p className="font-bold text-gray-900">
              Connected to: {productionHouse.production_house?.name || 'Production House'}
            </p>
            <p className="text-sm text-gray-600">
              Projects assigned by your production house admin will appear here. You can also request to join existing projects.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-900 rounded font-medium">
          {error}
        </div>
      )}

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border-2 border-teal-100 shadow">
          <Film className="mx-auto mb-3 text-teal-400" size={48} />
          <p className="text-xl font-bold text-gray-900">No projects yet</p>
          <p className="text-gray-500 mt-1 text-sm">
            {productionHouse
              ? 'Request your admin to allocate you to a project, or wait for assignment.'
              : 'Add your first project to get started.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {projects.map((project) => {
            const cfg = statusConfig[project.status] || statusConfig['active'];
            const { Icon } = cfg;
            const progress = project.progress ?? 0;
            const deadline = project.deadline
              ? new Date(project.deadline).toLocaleDateString()
              : project.end_date
                ? new Date(project.end_date).toLocaleDateString()
                : null;

            return (
              <div key={project.enthusiast_project_id}
                className={`bg-white p-5 rounded-xl shadow-lg border-2 ${cfg.border} hover:shadow-xl transition-all`}>
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-bold text-gray-900">{project.project_name}</h3>
                  <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${cfg.bg} ${cfg.color}`}>
                    <Icon size={12} /> {cfg.label}
                  </span>
                </div>

                <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
                  {project.role && <span>🎭 <span className="font-semibold text-gray-700">{project.role}</span></span>}
                  {deadline && <span>📅 <span className="font-semibold text-gray-700">{deadline}</span></span>}
                </div>

                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 font-medium">Progress</span>
                    <span className="font-bold text-gray-900">{progress}%</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${cfg.bar}`}
                      style={{ width: `${progress}%` }} />
                  </div>
                </div>

                <button
                  onClick={() => {
                    setSelectedProject(project);
                    setNewProgress(String(progress));
                    setNewStatus(project.status);
                  }}
                  className="w-full py-2 rounded-lg bg-teal-600 text-white font-bold hover:bg-teal-700 transition-colors text-sm shadow"
                >
                  Update Progress
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ===================== UPDATE PROGRESS MODAL ===================== */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl border-2 border-teal-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Update Project</h3>
              <button onClick={() => setSelectedProject(null)}>
                <X size={24} className="text-gray-500" />
              </button>
            </div>
            <p className="text-gray-600 mb-4 font-medium">{selectedProject.project_name}</p>
            <form onSubmit={handleUpdateProject} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Progress (0-100)</label>
                <input type="number" value={newProgress}
                  onChange={(e) => setNewProgress(e.target.value)}
                  min="0" max="100" required
                  className="w-full px-4 py-2 border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Status</label>
                <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900">
                  <option value="active">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={updating}
                  className="flex-1 py-2 bg-teal-600 text-white rounded-lg font-bold hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {updating ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : 'Save'}
                </button>
                <button type="button" onClick={() => setSelectedProject(null)}
                  className="flex-1 py-2 bg-gray-100 text-gray-800 rounded-lg font-bold border-2 border-gray-300">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===================== ADD PROJECT MODAL (No PH) ===================== */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl border-2 border-teal-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Add New Project</h3>
              <button onClick={() => setShowAddForm(false)}>
                <X size={24} className="text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleAddProject} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Project Name</label>
                <input type="text" value={addForm.project_name}
                  onChange={(e) => setAddForm({ ...addForm, project_name: e.target.value })}
                  required placeholder="Enter project name"
                  className="w-full px-4 py-2 border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-800 mb-1">Your Role</label>
                <input type="text" value={addForm.role}
                  onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}
                  placeholder="e.g. Actor, Director"
                  className="w-full px-4 py-2 border-2 border-teal-300 rounded-lg focus:border-teal-500 outline-none text-gray-900"
                />
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={adding}
                  className="flex-1 py-2 bg-teal-600 text-white rounded-lg font-bold hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {adding ? <><Loader2 size={16} className="animate-spin" /> Adding...</> : 'Add Project'}
                </button>
                <button type="button" onClick={() => setShowAddForm(false)}
                  className="flex-1 py-2 bg-gray-100 text-gray-800 rounded-lg font-bold border-2 border-gray-300">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===================== REQUEST ALLOCATION MODAL (PH Connected) ===================== */}
      {showRequestForm && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '16px'
        }}>
          <div style={{
            backgroundColor: '#ffffff', borderRadius: '12px', padding: '24px',
            width: '100%', maxWidth: '480px', boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
            border: '2px solid #99f6e4'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                <Building2 size={22} color="#0d9488" /> Request Project Allocation
              </h3>
              <button onClick={() => setShowRequestForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} color="#6b7280" />
              </button>
            </div>

            {/* Info box */}
            <div style={{ backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px', padding: '12px', marginBottom: '16px', fontSize: '14px', color: '#374151' }}>
              Requesting to join a project at{' '}
              <strong style={{ color: '#0d9488' }}>
                {productionHouse?.production_house?.name || 'your production house'}
              </strong>. Your admin will review and approve this request.
            </div>

            {/* Success */}
            {requestSuccess && (
              <div style={{ backgroundColor: '#f0fdf4', borderLeft: '4px solid #22c55e', padding: '12px', borderRadius: '4px', marginBottom: '16px', color: '#166534', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckCircle size={18} /> {requestSuccess}
              </div>
            )}

            {/* Role input */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '700', color: '#1f2937', marginBottom: '6px' }}>
                Your Role
              </label>
              <input
                type="text"
                value={requestForm.role}
                onChange={(e) => setRequestForm({ ...requestForm, role: e.target.value })}
                placeholder="e.g. Actor, Cameraman, Editor"
                style={{ width: '100%', padding: '10px 14px', border: '2px solid #5eead4', borderRadius: '8px', outline: 'none', fontSize: '14px', color: '#111827', boxSizing: 'border-box' }}
              />
            </div>

            {/* Message input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '700', color: '#1f2937', marginBottom: '6px' }}>
                Message to Admin
              </label>
              <textarea
                value={requestForm.message}
                onChange={(e) => setRequestForm({ ...requestForm, message: e.target.value })}
                rows={4}
                required
                placeholder="Briefly explain why you'd like to join a project and what you bring to the team..."
                style={{ width: '100%', padding: '10px 14px', border: '2px solid #5eead4', borderRadius: '8px', outline: 'none', fontSize: '14px', color: '#111827', resize: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={handleRequestAllocation}
                disabled={requesting || !requestForm.message}
                style={{
                  flex: 1, padding: '10px 0',
                  backgroundColor: requesting || !requestForm.message ? '#9ca3af' : '#0d9488',
                  color: '#ffffff', borderRadius: '8px', fontWeight: '700',
                  border: 'none', cursor: requesting || !requestForm.message ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                }}
              >
                {requesting
                  ? <><Loader2 size={16} className="animate-spin" /> Sending...</>
                  : <><Send size={16} /> Send Request</>}
              </button>
              <button
                type="button"
                onClick={() => setShowRequestForm(false)}
                style={{
                  flex: 1, padding: '10px 0',
                  backgroundColor: '#588cf3', color: '#1f2937',
                  borderRadius: '8px', fontWeight: '700',
                  border: '2px solid #151618', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default MyProjects;
